import React from "react";
import { Box, Typography, Alert, Chip, Stack } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { AnnouncementResponseDto } from "../../shared/dtos/announcement/AnnouncementResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface ViewAnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  announcement: AnnouncementResponseDto | null;
  loading: boolean;
  error: string | null;
}

const ViewAnnouncementModal: React.FC<ViewAnnouncementModalProps> = ({
  open,
  onClose,
  announcement,
  loading,
  error,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Announcement...
          </Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }
    if (!announcement) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Data not available.
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
          {announcement.title}
        </Typography>

        <Box
          sx={{
            p: 2,
            my: 2,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {announcement.content}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Author" value={announcement.authorName || "N/A"} />
          <DetailItem
            label="Audience"
            value={announcement.audience.replace(/_/g, " ")}
          />
          <DetailItem
            label="Publish Date"
            value={formatDateForDisplay(announcement.publishDate)}
          />
          <DetailItem
            label="Expiry Date"
            value={formatDateForDisplay(announcement.expiryDate)}
          />
        </Box>

        {announcement.targetClassrooms &&
          announcement.targetClassrooms.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "text.secondary", mb: 1, fontWeight: 600 }}
              >
                Target Classrooms
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 0.5 }}
              >
                {announcement.targetClassrooms.map((c) => (
                  <Chip
                    key={c.id}
                    label={c.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}

        {announcement.targetGradeLevels &&
          announcement.targetGradeLevels.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "text.secondary", mb: 1, fontWeight: 600 }}
              >
                Target Grade Levels
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 0.5 }}
              >
                {announcement.targetGradeLevels.map((g) => (
                  <Chip
                    key={g.id}
                    label={g.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Announcement Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewAnnouncementModal;
